import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { VerticalStepperStep } from '../../../../../components/verticalStepper/verticalStepper.component';
import AdmissionStep4FeesView, {
  Step4FeePersistPayload,
} from './AdmissionSteps/AdmissionStep4.Fees.view';
import AdmissionStep3GradeView, {
  Step3FormData,
} from './AdmissionSteps/AdmissionStep3.Grade.view';
import AdmissionStep2GuardianDetailsView, {
  Step2FormData,
} from './AdmissionSteps/AdmissionStep2.GuardianDetails.view';
import AdmissionStep1BasicDetailsView, {
  Step1FormData,
} from './AdmissionSteps/AdmissionStep1.BasicDetails.view';
import { TStudentDetails } from '../../../../../../apis/students/students.interfaces';
import { Step3Params } from './studentAdmission.interfaces';
import { useStudentsService } from '../../../../../../apis/students/useStudentsService';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import {
  getAdmissionInstituteId,
  mapGuardianFormToGuardianDetailsPayload,
  mapStep1StudentToBasicDetailsRequest,
  mapStep1StudentToPatchBasicDetails,
} from '../../../../../../apis/students/studentAdmissionApi.helpers';
import { TUserDetails } from 'src/apis/users/users.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

/** Immutable pair from POST /students/basic-details — survives partial studentDetails hydration */
type TAdmissionPair = { studentId: string; userId: string };

const warnAdmissionIdDrift = (
  step1PairRef: MutableRefObject<TAdmissionPair | null>,
  sid: string | null,
  uid: string | null
): void => {
  if (process.env.NODE_ENV !== 'development' || !step1PairRef.current) {
    return;
  }
  const p = step1PairRef.current;
  if (sid && sid !== p.studentId) {
    // eslint-disable-next-line no-console
    console.warn('[admission] studentId differs from basic-details pair', {
      resolved: sid,
      step1: p.studentId,
    });
  }
  if (uid && uid !== p.userId) {
    // eslint-disable-next-line no-console
    console.warn('[admission] userId differs from basic-details pair', {
      resolved: uid,
      step1: p.userId,
    });
  }
};

export const useStudentAdmissionStepsHook = (
  studentDetails: TStudentDetails | null
) => {
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>({});
  const [step3Data, setStep3Data] = useState<Step3Params | null>(null);
  const step1AdmissionPairRef = useRef<TAdmissionPair | null>(null);

  const [draftStudentId, setDraftStudentId] = useState<string | null>(() =>
    studentDetails?.feeDetails?.studentId ?? null
  );
  const [draftUserId, setDraftUserId] = useState<string | null>(() =>
    studentDetails?.id ?? studentDetails?.feeDetails?.userId ?? null
  );

  const step1Ref = useRef<{ submitStep: () => Promise<boolean> }>(null);
  const step2Ref = useRef<{ submitStep: () => Promise<boolean> }>(null);
  const step3Ref = useRef<{ submitStep: () => Promise<boolean> }>(null);
  const step4Ref = useRef<{ submitStep: () => Promise<boolean> }>(null);

  const {
    createStudentWithBasicDetails,
    patchStudentBasicDetails,
    updateStudentGuardianDetails,
    updateStudentGradeDetails,
    updateStudentFeeDetails,
  } = useStudentsService();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const isMockData = isAppWithMock();

  /** Hydrate from loaded profile without overwriting IDs already set by POST basic-details */
  useEffect(() => {
    if (!studentDetails) {
      return;
    }
    setDraftStudentId((prev) =>
      prev ?? studentDetails.feeDetails?.studentId ?? null
    );
    setDraftUserId((prev) =>
      prev ??
      studentDetails.id ??
      studentDetails.feeDetails?.userId ??
      null
    );
  }, [studentDetails]);

  useEffect(() => {
    if (studentDetails?.grade && studentDetails?.category) {
      setStep3Data((previousData) => {
        if (
          previousData?.grade === studentDetails.grade &&
          previousData?.section === (studentDetails.section ?? '') &&
          previousData?.category === studentDetails.category
        ) {
          return previousData;
        }
        return {
          grade: studentDetails.grade,
          section: studentDetails.section ?? '',
          category: studentDetails.category,
        };
      });
    }
  }, [studentDetails?.grade, studentDetails?.section, studentDetails?.category]);

  const updateGivenStep = useCallback((stepIndex: number, completed: boolean) => {
    setCompletedSteps((previousSteps) => {
      if (previousSteps[stepIndex] === completed) {
        return previousSteps;
      }
      return { ...previousSteps, [stepIndex]: completed };
    });
  }, []);

  const persistStep1 = useCallback(
    async (data: Step1FormData) => {
      if (studentDetails?.id) {
        return;
      }
      const existingStudentId =
        step1AdmissionPairRef.current?.studentId ?? draftStudentId;
      const existingUserId =
        step1AdmissionPairRef.current?.userId ?? draftUserId;

      // Re-submitting Step 1 once a draft exists (e.g. Back → edit → Continue)
      // must PATCH the existing draft, not POST a new one — POST is create-only
      // and would spawn a duplicate draft student.
      if (existingStudentId) {
        const patchBody = mapStep1StudentToPatchBasicDetails({
          studentId: existingStudentId,
          userId: existingUserId ?? undefined,
          student: data.student as TUserDetails,
        });
        await patchStudentBasicDetails(patchBody);
        return;
      }

      const body = mapStep1StudentToBasicDetailsRequest({
        student: data.student as TUserDetails,
        createdBy: loggedInUserId,
        profilePictureRef: {},
      });
      const res = await createStudentWithBasicDetails(body);
      step1AdmissionPairRef.current = {
        studentId: res.studentId,
        userId: res.userId,
      };
      setDraftStudentId(res.studentId);
      setDraftUserId(res.userId);
    },
    [
      studentDetails?.id,
      draftStudentId,
      draftUserId,
      loggedInUserId,
      createStudentWithBasicDetails,
      patchStudentBasicDetails,
    ]
  );

  const resolvePersistIds = useCallback(() => {
    const sid =
      step1AdmissionPairRef.current?.studentId ??
      draftStudentId ??
      studentDetails?.feeDetails?.studentId ??
      null;
    const uid =
      step1AdmissionPairRef.current?.userId ??
      draftUserId ??
      studentDetails?.id ??
      studentDetails?.feeDetails?.userId ??
      null;
    warnAdmissionIdDrift(step1AdmissionPairRef, sid, uid);
    return { sid, uid };
  }, [
    draftStudentId,
    draftUserId,
    studentDetails?.feeDetails?.studentId,
    studentDetails?.id,
    studentDetails?.feeDetails?.userId,
  ]);

  const persistStep2 = useCallback(
    async (data: Step2FormData) => {
      const { sid, uid } = resolvePersistIds();
      if (!sid || !uid) {
        throw new Error('Student draft not ready');
      }
      const instituteId = getAdmissionInstituteId();
      const guardians = mapGuardianFormToGuardianDetailsPayload({
        guardian1: data.guardian1,
        guardian2: data.guardian2,
      });
      await updateStudentGuardianDetails({
        studentId: sid,
        userId: uid,
        guardianDetails1: guardians.guardianDetails1,
        ...(guardians.guardianDetails2
          ? { guardianDetails2: guardians.guardianDetails2 }
          : {}),
        createdBy: loggedInUserId,
        instituteId,
      });
    },
    [resolvePersistIds, loggedInUserId, updateStudentGuardianDetails]
  );

  const persistStep3 = useCallback(
    async (data: Step3FormData) => {
      const { sid, uid } = resolvePersistIds();
      const instituteId = getAdmissionInstituteId();
      const sectionId = data.section?.trim();
      if (!sectionId) {
        throw new Error('Section placement is required');
      }
      if (!sid || !uid) {
        throw new Error('Student draft not ready');
      }
      await updateStudentGradeDetails({
        studentId: sid,
        userId: uid,
        instituteId,
        sectionId,
        createdBy: loggedInUserId,
      });
    },
    [resolvePersistIds, loggedInUserId, updateStudentGradeDetails]
  );

  const persistStep4 = useCallback(
    async (payload: Step4FeePersistPayload) => {
      const { sid, uid } = resolvePersistIds();
      if (!sid || !uid) {
        throw new Error('Student draft not ready');
      }
      const instituteId = getAdmissionInstituteId();
      await updateStudentFeeDetails({
        studentId: sid,
        userId: uid,
        instituteId,
        feeComponents: payload.feeComponents,
        createdBy: loggedInUserId,
      });
    },
    [resolvePersistIds, loggedInUserId, updateStudentFeeDetails]
  );

  const onStep3GradeComplete = useCallback(
    (data?: Step3Params) => {
      if (data?.grade && data?.category) {
        setStep3Data((previousData) => {
          const section = data.section ?? '';
          if (
            previousData?.grade === data.grade &&
            previousData?.section === section &&
            previousData?.category === data.category
          ) {
            return previousData;
          }
          return { grade: data.grade, section, category: data.category };
        });
      }
      updateGivenStep(2, true);
    },
    [updateGivenStep]
  );

  const noop = useCallback(() => undefined, []);

  const steps = useMemo<VerticalStepperStep[]>(() => {
    const persistEnabled = !isMockData;

    return [
      {
        label: 'Basic Details',
        component: (
          <AdmissionStep1BasicDetailsView
            ref={step1Ref}
            onCompleteHandler={
              isMockData ? () => updateGivenStep(0, true) : noop
            }
            studentDetails={studentDetails ?? null}
            onStepValidityChange={(v) => updateGivenStep(0, v)}
            onPersistStep={
              persistEnabled && !studentDetails?.id ? persistStep1 : undefined
            }
          />
        ),
        description: 'Enter the basic details of the student',
        completed: completedSteps[0] ?? false,
        completeAction: async () => {
          const ok = await step1Ref.current?.submitStep();
          return ok ?? false;
        },
      },
      {
        label: 'Guardian Details',
        component: (
          <AdmissionStep2GuardianDetailsView
            ref={step2Ref}
            onCompleteHandler={
              isMockData ? () => updateGivenStep(1, true) : noop
            }
            studentDetails={studentDetails ?? null}
            onStepValidityChange={(v) => updateGivenStep(1, v)}
            onPersistStep={persistEnabled ? persistStep2 : undefined}
          />
        ),
        description: 'Enter the guardian details of the student',
        completed: completedSteps[1] ?? false,
        completeAction: async () => {
          const ok = await step2Ref.current?.submitStep();
          return ok ?? false;
        },
      },
      {
        label: 'Grade',
        component: (
          <AdmissionStep3GradeView
            ref={step3Ref}
            onCompleteHandler={onStep3GradeComplete}
            studentDetails={studentDetails ?? null}
            onStepValidityChange={(v) => updateGivenStep(2, v)}
            onPersistStep={persistEnabled ? persistStep3 : undefined}
          />
        ),
        description: 'Select the grade for the student',
        completed: completedSteps[2] ?? false,
        completeAction: async () => {
          const ok = await step3Ref.current?.submitStep();
          return ok ?? false;
        },
      },
      {
        label: 'Fees',
        component: (
          <AdmissionStep4FeesView
            ref={step4Ref}
            onCompleteHandler={() => updateGivenStep(3, true)}
            studentDetails={studentDetails ?? null}
            step3Data={step3Data}
            onStepValidityChange={(v) => updateGivenStep(3, v)}
            onPersistStep={persistEnabled ? persistStep4 : undefined}
          />
        ),
        description: 'Fee details',
        completed: completedSteps[3] ?? false,
        completeAction: async () => {
          const ok = await step4Ref.current?.submitStep();
          return ok ?? false;
        },
      },
    ];
  }, [
    completedSteps,
    isMockData,
    noop,
    onStep3GradeComplete,
    persistStep1,
    persistStep2,
    persistStep3,
    persistStep4,
    step3Data,
    studentDetails,
    updateGivenStep,
  ]);

  return {
    steps,
    /** Enrollment `studentId` for finalize — not the student user id */
    admissionStudentId:
      draftStudentId ??
      studentDetails?.feeDetails?.studentId ??
      null,
  };
};
